import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import { createPortfolioDateFormatter, DEFAULT_TIME_ZONE } from '../../utils/dateTime';

const HISTORY_DAYS = 90;
const MS_PER_DAY = 1000 * 60 * 60 * 24;
const HEIGHT = 280;
const RAIN_COLOR = '#4472C4';
const PM10_COLOR = '#ED7D31';
const PM25_COLOR = '#008900';
const RAIN_TICKS = Object.freeze([0, 2, 4, 6, 8, 10]);
const PM_TICKS = Object.freeze([0, 50, 100, 150, 200]);
const Y_AXIS_LABEL_FONT_SIZE = '10px';
const Y_AXIS_LABEL_FONT_FAMILY = 'Arial, Helvetica, sans-serif';

function startOfLocalDay(date) {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

function applyYAxisLabelStyle(selection) {
  return selection
    .style('font-size', Y_AXIS_LABEL_FONT_SIZE)
    .style('font-family', Y_AXIS_LABEL_FONT_FAMILY)
    .style('font-weight', 'bold')
    .attr('fill', '#000');
}

function formatRainTick(value) {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded}mm` : `${rounded.toFixed(1)}mm`;
}

export function buildPrecipitationPmHistory(days = HISTORY_DAYS, now = new Date()) {
  const endDate = startOfLocalDay(now);
  const startDate = new Date(endDate);
  startDate.setDate(endDate.getDate() - (days - 1));

  const rainDays = new Map([
    [8, 4.2],
    [19, 6.8],
    [31, 9.1],
    [44, 3.4],
    [58, 7.6],
    [72, 2.8],
    [84, 5.5],
  ]);

  const rows = [];
  let pm10 = 95;
  let pm25 = 42;

  for (let i = 0; i < days; i += 1) {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + i);
    const rain = rainDays.get(i) ?? 0;

    if (rain > 0) {
      pm10 = Math.max(20, pm10 - rain * 6);
      pm25 = Math.max(8, pm25 - rain * 3.2);
    } else {
      pm10 = Math.min(200, pm10 + 1.4 + Math.sin(i / 6) * 2.2);
      pm25 = Math.min(200, pm25 + 0.7 + Math.sin(i / 5.2) * 1.6 + (i % 11 === 0 ? 8 : 0));
    }

    rows.push({
      date,
      rain: Math.round(rain * 10) / 10,
      pm10: Math.round(pm10),
      pm25: Math.round(pm25),
    });
  }

  return { startDate, endDate, rows };
}

export default function PrecipitationParticulateMatter({
  onTooltip,
  timeZone = DEFAULT_TIME_ZONE,
}) {
  const dateFormatter = useMemo(
    () => createPortfolioDateFormatter(timeZone),
    [timeZone],
  );
  const history = useMemo(() => buildPrecipitationPmHistory(), []);
  const svgRef = useRef(null);
  const wrapperRef = useRef(null);
  const onTooltipRef = useRef(onTooltip);
  onTooltipRef.current = onTooltip;
  const keyboardIndexRef = useRef(0);
  const keyboardModeRef = useRef(false);
  const reactId = useId().replace(/:/g, '');
  const [dimensions, setDimensions] = useState({ width: 800, height: HEIGHT });

  useEffect(() => {
    if (!wrapperRef.current) return undefined;
    const el = wrapperRef.current;
    const update = () => {
      const { width } = el.getBoundingClientRect();
      setDimensions({ width: Math.max(0, Math.floor(width)), height: HEIGHT });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    el.addEventListener('transitionend', update);
    return () => {
      ro.disconnect();
      el.removeEventListener('transitionend', update);
    };
  }, []);

  useEffect(() => {
    if (!svgRef.current || !history.rows.length) return undefined;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const { width, height } = dimensions;
    const margin = { top: 20, right: 56, bottom: 48, left: 56 };
    const plotLeft = margin.left;
    const plotRight = width - margin.right;
    const plotTop = margin.top;
    const plotBottom = height - margin.bottom;
    const plotW = Math.max(0, plotRight - plotLeft);
    const plotH = Math.max(0, plotBottom - plotTop);
    const rows = history.rows;
    const yTickPadding = 10;

    const xMin = d3.timeDay.floor(history.startDate);
    const xMax = d3.timeDay.floor(history.endDate);
    const xScale = d3
      .scaleTime()
      .domain([xMin, d3.timeDay.offset(xMax, 1)])
      .range([plotLeft, plotRight]);

    const yRain = d3.scaleLinear().domain([0, 10]).range([plotBottom, plotTop]);
    const yPm = d3.scaleLinear().domain([0, 200]).range([plotBottom, plotTop]);

    const dayWidth = Math.max(1, xScale(d3.timeDay.offset(xMin, 1)) - xScale(xMin));
    const barWidth = Math.max(3, Math.min(dayWidth * 0.7, dayWidth - 1));

    const dayTicks = d3.timeDay.range(xMin, d3.timeDay.offset(xMax, 1));
    const startMs = xMin.getTime();
    const isLabeledTick = (d) =>
      Math.round((d.getTime() - startMs) / MS_PER_DAY) % 7 === 0;

    const xAxis = d3
      .axisBottom(xScale)
      .tickValues(dayTicks)
      .tickFormat((d) => (isLabeledTick(d) ? dateFormatter.formatAxisMdY(d) : ''));

    const xAxisGroup = svg
      .append('g')
      .attr('transform', `translate(0,${plotBottom})`)
      .call(xAxis);

    xAxisGroup
      .selectAll('text')
      .style('text-anchor', 'start')
      .style('font-weight', 'bold')
      .attr('dx', '0.5em');
    xAxisGroup.selectAll('.tick line').attr('y2', (d) => (isLabeledTick(d) ? 12 : 5));

    const yLeft = d3
      .axisLeft(yRain)
      .tickValues(RAIN_TICKS)
      .tickPadding(yTickPadding)
      .tickSizeInner(0)
      .tickFormat(formatRainTick);

    const yRight = d3
      .axisRight(yPm)
      .tickValues(PM_TICKS)
      .tickPadding(yTickPadding)
      .tickSizeInner(0)
      .tickFormat((d) => String(d));

    applyYAxisLabelStyle(
      svg.append('g').attr('transform', `translate(${plotLeft},0)`).call(yLeft).selectAll('text'),
    );
    applyYAxisLabelStyle(
      svg.append('g').attr('transform', `translate(${plotRight},0)`).call(yRight).selectAll('text'),
    );

    PM_TICKS.forEach((value) => {
      svg
        .append('line')
        .attr('x1', plotLeft)
        .attr('x2', plotRight)
        .attr('y1', yPm(value))
        .attr('y2', yPm(value))
        .attr('stroke', '#d9d9d9')
        .attr('stroke-width', 1);
    });

    const clipId = `precip-pm-clip-${reactId}`;
    const rainGradId = `precip-rain-grad-${reactId}`;
    const defs = svg.append('defs');
    defs
      .append('clipPath')
      .attr('id', clipId)
      .append('rect')
      .attr('x', plotLeft)
      .attr('y', plotTop)
      .attr('width', plotW)
      .attr('height', plotH);

    const rainGrad = defs
      .append('linearGradient')
      .attr('id', rainGradId)
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '0%')
      .attr('y2', '100%');
    rainGrad.append('stop').attr('offset', '0%').attr('stop-color', RAIN_COLOR);
    rainGrad.append('stop').attr('offset', '100%').attr('stop-color', '#000');

    const plot = svg.append('g').attr('clip-path', `url(#${clipId})`);

    plot
      .selectAll('.rain-bar')
      .data(rows.filter((d) => d.rain > 0))
      .enter()
      .append('rect')
      .attr('class', 'rain-bar')
      .attr('x', (d) => xScale(d.date) - barWidth / 2)
      .attr('y', (d) => yRain(d.rain))
      .attr('width', barWidth)
      .attr('height', (d) => Math.max(0, yRain(0) - yRain(d.rain)))
      .attr('fill', `url(#${rainGradId})`);

    const linePm10 = d3
      .line()
      .x((d) => xScale(d.date))
      .y((d) => yPm(d.pm10))
      .curve(d3.curveMonotoneX);

    const linePm25 = d3
      .line()
      .x((d) => xScale(d.date))
      .y((d) => yPm(d.pm25))
      .curve(d3.curveMonotoneX);

    plot
      .append('path')
      .datum(rows)
      .attr('fill', 'none')
      .attr('stroke', PM10_COLOR)
      .attr('stroke-width', 2)
      .attr('vector-effect', 'non-scaling-stroke')
      .attr('d', linePm10);

    plot
      .append('path')
      .datum(rows)
      .attr('fill', 'none')
      .attr('stroke', PM25_COLOR)
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '6,4')
      .attr('vector-effect', 'non-scaling-stroke')
      .attr('d', linePm25);

    const crosshair = svg
      .append('g')
      .style('pointer-events', 'none')
      .style('opacity', 0);

    const crosshairV = crosshair
      .append('line')
      .attr('stroke', 'rgba(0,0,0,0.35)')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4,3')
      .attr('vector-effect', 'non-scaling-stroke');

    const nearestIndexByPx = (px) => {
      let best = 0;
      let bestDx = Infinity;
      for (let i = 0; i < rows.length; i += 1) {
        const x = xScale(rows[i].date);
        const dx = Math.abs(x - px);
        if (dx < bestDx) {
          bestDx = dx;
          best = i;
        }
      }
      return best;
    };

    const tooltipContentFor = (d) => (
      <>
        <strong>X:</strong> {dateFormatter.formatAxisMdY(d.date)}
        <div>
          <span style={{ color: RAIN_COLOR }}>■</span> <strong>Rain:</strong> {formatRainTick(d.rain)}
        </div>
        <div>
          <span style={{ color: PM10_COLOR }}>●</span> <strong>PM10:</strong> {d.pm10} μg/m³
        </div>
        <div>
          <span style={{ color: PM25_COLOR }}>●</span> <strong>PM2.5:</strong> {d.pm25} μg/m³
        </div>
      </>
    );

    const showSelectionByIndex = (idx) => {
      const clamped = Math.max(0, Math.min(rows.length - 1, idx));
      keyboardIndexRef.current = clamped;
      const d = rows[clamped];
      const xSnap = xScale(d.date);
      crosshair.style('opacity', 1);
      crosshairV
        .attr('x1', xSnap)
        .attr('x2', xSnap)
        .attr('y1', plotTop)
        .attr('y2', plotBottom);

      const tip = onTooltipRef.current;
      const svgEl = svgRef.current;
      if (tip && svgEl) {
        const rect = svgEl.getBoundingClientRect();
        const topY = Math.min(yRain(d.rain || 0), yPm(d.pm10), yPm(d.pm25));
        tip({
          visible: true,
          content: tooltipContentFor(d),
          x: rect.left + xSnap,
          y: rect.top + topY - 12,
        });
      }
    };

    const hideSelection = () => {
      crosshair.style('opacity', 0);
      const tip = onTooltipRef.current;
      if (tip) tip((t) => ({ ...t, visible: false }));
    };

    svg
      .append('rect')
      .attr('class', 'hover-overlay')
      .attr('x', plotLeft)
      .attr('y', plotTop)
      .attr('width', plotW)
      .attr('height', plotH)
      .attr('fill', 'transparent')
      .style('cursor', 'crosshair')
      .on('pointerenter', (event) => {
        keyboardModeRef.current = false;
        showSelectionByIndex(nearestIndexByPx(d3.pointer(event, svgRef.current)[0]));
      })
      .on('pointermove', (event) => {
        keyboardModeRef.current = false;
        showSelectionByIndex(nearestIndexByPx(d3.pointer(event, svgRef.current)[0]));
      });

    const svgEl = svgRef.current;
    d3.select(svgEl).on('pointerleave', () => {
      if (document.activeElement === svgEl) {
        keyboardModeRef.current = true;
        showSelectionByIndex(keyboardIndexRef.current);
      } else {
        hideSelection();
      }
    });

    const handleKeyDown = (e) => {
      if (!keyboardModeRef.current) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (keyboardIndexRef.current > 0) showSelectionByIndex(keyboardIndexRef.current - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (keyboardIndexRef.current < rows.length - 1) {
          showSelectionByIndex(keyboardIndexRef.current + 1);
        }
      }
    };
    const handleFocus = () => {
      keyboardModeRef.current = true;
      showSelectionByIndex(keyboardIndexRef.current);
    };
    const handleBlur = () => {
      keyboardModeRef.current = false;
      hideSelection();
    };

    svgEl.addEventListener('keydown', handleKeyDown);
    svgEl.addEventListener('focus', handleFocus);
    svgEl.addEventListener('blur', handleBlur);
    if (document.activeElement === svgEl) handleFocus();

    return () => {
      svgEl.removeEventListener('keydown', handleKeyDown);
      svgEl.removeEventListener('focus', handleFocus);
      svgEl.removeEventListener('blur', handleBlur);
    };
  }, [dimensions, history, dateFormatter, reactId]);

  return (
    <div
      ref={wrapperRef}
      className="precipitation-particulate-matter"
      style={{ width: '100%', height: 'auto', minWidth: 0 }}
    >
      <svg
        ref={svgRef}
        tabIndex={0}
        role="img"
        aria-label="Precipitation and particulate matter chart. Use left and right arrow keys to navigate between days."
        style={{ width: '100%', height: `${HEIGHT}px`, display: 'block' }}
      />
      <div className="chart-legend">
        <span style={{ color: RAIN_COLOR }}>
          <svg width={10} height={14} aria-hidden="true">
            <defs>
              <linearGradient id={`legend-rain-${reactId}`} x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor={RAIN_COLOR} />
                <stop offset="100%" stopColor="#000" />
              </linearGradient>
            </defs>
            <rect x={2} y={0} width={6} height={14} fill={`url(#legend-rain-${reactId})`} />
          </svg>
          Rain mm/day
        </span>
        <span style={{ color: PM10_COLOR }}>
          <svg width={28} height={10} aria-hidden="true">
            <line x1={2} y1={5} x2={26} y2={5} stroke={PM10_COLOR} strokeWidth={2} />
          </svg>
          PM10 μg/m³
        </span>
        <span style={{ color: PM25_COLOR }}>
          <svg width={28} height={10} aria-hidden="true">
            <line
              x1={2}
              y1={5}
              x2={26}
              y2={5}
              stroke={PM25_COLOR}
              strokeWidth={2}
              strokeDasharray="6,4"
            />
          </svg>
          PM2.5 μg/m³
        </span>
      </div>
    </div>
  );
}
